import React, { useState, useEffect } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import Toast from '../global/Toast';
import { BRANDING } from '../config';
import '../global/styles.css';

// Rows hold either an object path (current) or a legacy public URL; both map
// to the object path inside the signatures bucket.
const signaturePath = (value) => {
  if (!value) return null;
  const marker = '/storage/v1/object/public/signatures/';
  const i = value.indexOf(marker);
  return i >= 0 ? decodeURIComponent(value.slice(i + marker.length)) : value;
};

function AccountabilitySubmissions({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  // Signatures live in a private bucket: rendered through short-lived signed URLs.
  const [signedUrls, setSignedUrls] = useState({});

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const { data, error } = await supabase
        .from('complete_usage_accountability')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSubmissions(data || []);

      const paths = [...new Set((data || []).map(r => signaturePath(r.paramedic_signature_url)).filter(Boolean))];
      if (paths.length) {
        const { data: signed } = await supabase.storage.from('signatures').createSignedUrls(paths, 3600);
        const byPath = {};
        (signed || []).forEach(s => { if (s.signedUrl) byPath[s.path] = s.signedUrl; });
        setSignedUrls(byPath);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!submissions.length) {
      showToast('No data available to export.', 'warning');
      return;
    }

    const headers = [
      'Date',
      'Station',
      'Patient Name',
      'Incident Number',
      'Medication',
      'Total Amount',
      'Route',
      'Paramedic Name',
      'Submitted At'
    ];

    const rows = submissions.map(sub => [
      sub.form_date,
      sub.station,
      sub.patient_name,
      sub.incident_number,
      sub.medication,
      sub.total_amount_administered,
      sub.route_administered,
      sub.paramedic_name,
      new Date(sub.created_at).toLocaleString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `accountability_submissions_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const formatMedication = (med) => {
    const medications = {
      'morphine_sulfate': 'Morphine Sulfate',
      'fentanyl': 'Fentanyl',
      'ketamine': 'Ketamine',
      'ketamine_500mg': 'Ketamine 500mg/5mL',
      'midazolam': 'Midazolam 5mg/mL'
    };
    return medications[med] || med;
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--background-color)', padding: '20px' }}>
      <HamburgerMenu
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      <div style={{ maxWidth: '1200px', margin: '0 auto', paddingTop: '20px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '15px' }}>
          <h1 style={{
            color: 'var(--text-color)',
            fontSize: '24px',
            fontWeight: '600',
            margin: 0
          }}>
            Accountability Form Submissions
          </h1>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={exportToCSV}
              className="btn btn-primary"
              style={{ padding: '10px 20px' }}
            >
              Export to CSV
            </button>
            <button
              onClick={() => onNavigate('accountability-forms')}
              className="btn btn-secondary"
              style={{ padding: '10px 20px' }}
            >
              Back
            </button>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '50px', color: 'var(--text-muted)' }}>
            Loading submissions...
          </div>
        ) : submissions.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '50px',
            background: 'var(--card-background)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)'
          }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '16px' }}>No submissions found.</p>
            <button
              onClick={() => onNavigate('accountability-forms')}
              className="btn btn-primary"
              style={{ marginTop: '15px' }}
            >
              Submit New Form
            </button>
          </div>
        ) : (
          <div style={{
            background: 'var(--card-background)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            overflow: 'hidden'
          }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Station</th>
                    <th style={thStyle}>Patient</th>
                    <th style={thStyle}>Incident #</th>
                    <th style={thStyle}>Medication</th>
                    <th style={thStyle}>Amount</th>
                    <th style={thStyle}>Route</th>
                    <th style={thStyle}>Paramedic</th>
                    <th style={thStyle}>Signature</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((sub, index) => (
                    <tr
                      key={sub.id}
                      style={{
                        background: index % 2 === 0 ? 'white' : '#fafafa',
                        borderBottom: '1px solid var(--border-color)'
                      }}
                    >
                      <td style={tdStyle}>{sub.form_date}</td>
                      <td style={tdStyle}>{sub.station}</td>
                      <td style={tdStyle}>{sub.patient_name}</td>
                      <td style={tdStyle}>{sub.incident_number}</td>
                      <td style={tdStyle}>{formatMedication(sub.medication)}</td>
                      <td style={tdStyle}>{sub.total_amount_administered}</td>
                      <td style={tdStyle}>{sub.route_administered}</td>
                      <td style={tdStyle}>{sub.paramedic_name}</td>
                      <td style={tdStyle}>
                        {signedUrls[signaturePath(sub.paramedic_signature_url)] ? (
                          <img
                            src={signedUrls[signaturePath(sub.paramedic_signature_url)]}
                            alt="Signature"
                            style={{
                              maxWidth: '80px',
                              maxHeight: '40px',
                              cursor: 'pointer',
                              border: '1px solid var(--border-color)',
                              borderRadius: '4px'
                            }}
                            onClick={() => setSelectedImage(signedUrls[signaturePath(sub.paramedic_signature_url)])}
                          />
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Record count */}
            <div style={{
              padding: '15px 20px',
              borderTop: '1px solid var(--border-color)',
              color: 'var(--text-muted)',
              fontSize: '13px'
            }}>
              Showing {submissions.length} record{submissions.length !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div
          onClick={() => setSelectedImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            cursor: 'pointer'
          }}
        >
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '12px',
            maxWidth: '90%',
            maxHeight: '90%'
          }}>
            <img
              src={selectedImage}
              alt="Signature"
              style={{ maxWidth: '100%', maxHeight: '80vh' }}
            />
            <p style={{ textAlign: 'center', marginTop: '10px', color: 'var(--text-muted)' }}>
              Click anywhere to close
            </p>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
}

const thStyle = {
  padding: '15px 12px',
  textAlign: 'left',
  fontWeight: '600',
  color: '#333',
  borderBottom: '2px solid var(--border-color)',
  whiteSpace: 'nowrap'
};

const tdStyle = {
  padding: '12px',
  color: 'var(--text-color)'
};

export default AccountabilitySubmissions;
